import React, { useEffect, useState, useCallback } from "react";

export default function Subscribers() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [file, setFile] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load subscribers
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `https://app.saravanabhava.in/backend/subscribers.php?search=${encodeURIComponent(search)}`
      );
      const data = await r.json();
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load subscribers:", err);
      setError("⚠️ Failed to load subscribers.");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      load();
    }, 500);
    return () => clearTimeout(timeout);
  }, [search, load]);

  // Add single subscriber
  const add = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("https://app.saravanabhava.in/backend/subscribers.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!data.success) {
        setError(`❌ ${data.error}`);
        return;
      }

      setForm({ name: "", email: "", company: "" });
      load();
    } catch (err) {
      setError("❌ Failed to add subscriber: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete subscriber (optimistic UI update)
  const remove = async (id) => {
    const oldList = [...list];
    setList(list.filter((s) => s.id !== id));
    try {
      await fetch(`https://app.saravanabhava.in/backend/subscribers.php?id=${id}`, {
        method: "DELETE",
      });
    } catch (err) {
      setError("❌ Failed to delete subscriber: " + err.message);
      setList(oldList); // rollback
    }
  };

  // Upload CSV
  const uploadCSV = async (e) => {
    e.preventDefault();
    if (!file) return setError("⚠️ Please select a CSV file first!");

    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("https://app.saravanabhava.in/backend/subscribers.php", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!data.success) {
        setError(`❌ ${data.error}`);
        return;
      }

      setFile(null);
      load();
    } catch (err) {
      setError("❌ Failed to upload CSV: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow space-y-4">
      <h1 className="text-2xl font-bold">Subscribers</h1>

      {/* Error message */}
      {error && <div className="text-red-600">{error}</div>}

      {/* Search */}
      <input
        className="border p-2 rounded w-full"
        placeholder="🔍 Search by name, email or company..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Add Single Subscriber */}
      <form onSubmit={add} className="flex gap-2 flex-wrap">
        <input
          className="border p-2 rounded"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          className="border p-2 rounded"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <input
          className="border p-2 rounded"
          placeholder="Company"
          value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
        />
        <button
          type="submit"
          disabled={submitting}
          className="bg-green-600 text-white px-4 rounded disabled:opacity-50"
        >
          {submitting ? "Adding..." : "Add"}
        </button>
      </form>

      {/* Upload CSV */}
      <form onSubmit={uploadCSV} className="flex gap-2 items-center">
        <input
          type="file"
          accept=".csv"
          key={file ? file.name : "file"} // resets input after upload
          onChange={(e) => setFile(e.target.files[0])}
        />
        <button
          type="submit"
          disabled={submitting}
          className="bg-blue-600 text-white px-4 rounded disabled:opacity-50"
        >
          {submitting ? "Uploading..." : "Upload CSV"}
        </button>
      </form>

      {/* Loading Spinner */}
      {loading && (
        <div className="text-center py-4 text-gray-500 animate-pulse">
          ⏳ Loading subscribers...
        </div>
      )}

      {/* No Results */}
      {!loading && list.length === 0 && (
        <div className="text-center py-4 text-gray-400">⚠️ No subscribers found.</div>
      )}

      {/* Subscriber List */}
      {!loading && list.length > 0 && (
        <table className="w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Company</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td className="p-2 border">{s.id}</td>
                <td className="p-2 border">{s.name}</td>
                <td className="p-2 border">{s.email}</td>
                <td className="p-2 border">{s.company}</td>
                <td className="p-2 border">{s.status}</td>
                <td className="p-2 border">
                  <button
                    type="button"
                    onClick={() => remove(s.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
